/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import styles from './BlogMediaContent.module.scss';
import cx from 'classnames';
import type { ContentStackPost } from '@/features/contentstack/interface';
import { CMSHeadline } from '@/features/contentstack/components/CMSHeadline/CMSHeadline';
import { CMSButton } from '@/features/contentstack/components/CMSButton/CMSButton';
import { View } from '@az/starc-ui';
import { CMSBlogRichText } from '../CMSBlogRichText/CMSBlogRichText';

type Options = {
  feature_cta:
    | NonNullable<ContentStackPost['feature_content']>['feature_cta']
    | NonNullable<
        NonNullable<ContentStackPost['post_content']>[number]['copy_media']
      >['feature_cta'];
  heading: NonNullable<ContentStackPost['feature_content']>['heading'];
  heading_underline: NonNullable<ContentStackPost['feature_content']>['heading_underline'];
  direction: NonNullable<ContentStackPost['feature_content']>['direction'];
  feature_text: NonNullable<ContentStackPost['feature_content']>['feature_text'];
};

type Props = {
  options: Options;
  mediaElement: React.ReactNode;
};

export function BlogMediaContent({ options, mediaElement }: Props) {
  const { heading, heading_underline, feature_text, feature_cta, direction } = options;

  return (
    <View direction={direction === 'Left' ? 'row' : 'row-reverse'} className={styles.container}>
      <View.Item className={cx(styles.contentContainer)}>
        {heading && (
          <View.Item className={styles.title}>
            <CMSHeadline underline={heading_underline} nested size="225">
              {heading}
            </CMSHeadline>
          </View.Item>
        )}
        <View.Item className={styles.description}>
          <CMSBlogRichText content={feature_text} />
        </View.Item>
        <View direction="row" justify="center" className={styles.buttons}>
          {feature_cta?.map((cta) => {
            if (!cta.link?.href || !cta.link?.title) {
              return null;
            }

            return (
              <View.Item key={cta.link.title} className={styles.button}>
                <CMSButton textClassName={styles.buttonText} options={cta} />
              </View.Item>
            );
          })}
        </View>
      </View.Item>
      <View.Item className={styles.imageContainer}>{mediaElement}</View.Item>
    </View>
  );
}
